import { useState, useEffect } from 'react';
import { X, AlertTriangle, Info, AlertCircle, ArrowUpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMyPlan } from '@/services/apiClient';
import UpgradeRequestModal from './UpgradeRequestModal';

function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminPlanBanner() {
    const [planData, setPlanData] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    useEffect(() => {
        getMyPlan()
            .then(res => setPlanData(res.data))
            .catch(() => {});
    }, []);

    if (!planData) return null;

    const { company, plan } = planData;
    const sub = company?.subscription;
    const status = sub?.status;

    if (!status || status === 'active') return null;
    if (dismissed && status !== 'expired' && status !== 'cancelled') return null;

    const daysLeft = sub?.trialEndDate
        ? Math.max(0, Math.ceil((new Date(sub.trialEndDate) - Date.now()) / 86400000))
        : sub?.gracePeriodEndDate
        ? Math.max(0, Math.ceil((new Date(sub.gracePeriodEndDate) - Date.now()) / 86400000))
        : null;

    let config = null;

    if (status === 'trial') {
        const urgent = daysLeft !== null && daysLeft <= 3;
        config = {
            bg: urgent ? 'bg-amber-500/10 border-amber-500/30' : 'bg-blue-500/10 border-blue-500/30',
            text: urgent ? 'text-amber-700 dark:text-amber-300' : 'text-blue-700 dark:text-blue-300',
            Icon: urgent ? AlertTriangle : Info,
            message: urgent
                ? `Trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}! Upgrade now to avoid disruption.`
                : `Trial active — ${daysLeft !== null ? `${daysLeft} days remaining` : `ends ${formatDate(sub.trialEndDate)}`}. Upgrade to continue after your trial.`,
            dismissible: true,
        };
    } else if (status === 'grace_period') {
        config = {
            bg: 'bg-amber-500/10 border-amber-500/30',
            text: 'text-amber-700 dark:text-amber-300',
            Icon: AlertTriangle,
            message: `Grace period active — ${daysLeft !== null ? `${daysLeft} days left` : `ends ${formatDate(sub.gracePeriodEndDate)}`}. Your account will be blocked if no plan is assigned.`,
            dismissible: true,
        };
    } else if (status === 'expired' || status === 'cancelled') {
        config = {
            bg: 'bg-destructive/10 border-destructive/30',
            text: 'text-destructive',
            Icon: AlertCircle,
            message: 'Subscription expired. All actions are currently blocked. Contact your account manager to reactivate.',
            dismissible: false,
        };
    }

    if (!config) return null;

    const { bg, text, Icon, message, dismissible } = config;

    return (
        <>
            <div className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 mb-4 ${bg}`}>
                <Icon className={`h-4 w-4 shrink-0 ${text}`} />
                <p className={`flex-1 text-sm font-medium ${text}`}>{message}</p>
                <Button
                    size="sm"
                    variant="outline"
                    className={`h-7 shrink-0 gap-1.5 border-current text-xs ${text} hover:bg-black/10 dark:hover:bg-white/20`}
                    onClick={() => setUpgradeOpen(true)}
                >
                    <ArrowUpCircle className="h-3.5 w-3.5" />
                    Request Upgrade
                </Button>
                {dismissible && (
                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        className={`shrink-0 ${text} opacity-70 hover:opacity-100`}
                        aria-label="Dismiss"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>
            <UpgradeRequestModal
                open={upgradeOpen}
                onClose={() => setUpgradeOpen(false)}
                currentPlanSlug={plan?.slug}
            />
        </>
    );
}
